import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'

import { supabaseClient } from 'supabase'
import type { DeskWatchedValues } from 'components/events/EventForm/types'
import type { ApplicationRow } from './deskTypes'

dayjs.extend(utcPlugin)

/**
 * The client of `desk-recheck-match` (Phase 33.1): the desk asks the editor's
 * own match rules again with the town, dates and title in the desk form. The
 * function holds no secret and never calls a model; this client sends the
 * admin's own session through `functions.invoke` and never throws.
 */

/**
 * Why a re-check answered `unchecked`. Twin of desk-recheck-match's frozen
 * `RECHECK_REASONS` (a contract: add, never rename), pinned by a test.
 */
export const RECHECK_REASONS = Object.freeze([
  'no_start_date',
  'no_town',
  'no_title',
  'unknown_version',
  'no_match_policy',
  'shortlist_unavailable',
  'needs_model',
  'shortlist_full'
] as const)

export type DeskRecheckReason = (typeof RECHECK_REASONS)[number]

export type DeskRecheckLabel = 'possible_duplicate' | 'possible_update' | 'new' | 'unchecked'

const LABELS: ReadonlyArray<string> = ['possible_duplicate', 'possible_update', 'new', 'unchecked']

/** The request body (desk-recheck-match's parseRecheckBody). */
export type DeskRecheckBody = {
  application_id: number
  city_id: number | null
  start_date: string | null
  end_date: string | null
  start_time: string | null
  title: string | null
  subtitle: string | null
}

/** The 200 body. `reason` is set exactly when `label` is `unchecked`. */
export type DeskRecheckResponse = {
  label: DeskRecheckLabel
  target: { kind: 'event' | 'application'; id: number } | null
  reason: DeskRecheckReason | null
  written: boolean
  checked: { city_id: number | null; start_date: string | null }
}

/**
 * The function's refusal codes (its STATUS_BY_CODE, add never rename), plus
 * two the client adds: `network` (no response at all) and `unknown` (a
 * response that is not the contract).
 */
const ERROR_CODES = [
  'method_not_allowed',
  'unauthenticated',
  'not_admin',
  'payload_too_large',
  'bad_request',
  'not_found',
  'not_eligible',
  'already_decided',
  'misconfigured'
] as const

export type DeskRecheckErrorCode = (typeof ERROR_CODES)[number] | 'network' | 'unknown'

export type DeskRecheckOutcome =
  | { ok: true; value: DeskRecheckResponse }
  | { ok: false; code: DeskRecheckErrorCode; status: number | null }

/**
 * The match_by the desk's own re-check writes. Twin list: 0037's
 * applications_match_by_allowlist, editorFence's DESK_ONLY_MATCH_BY and the
 * Match panel's caption. Change all of them together.
 */
export const DESK_RULES_MATCH_BY = 'desk_rules' as const

/**
 * Code review WR-04: how long the desk waits for desk-recheck-match before
 * it aborts the request and answers `network` (retryable). Without it a
 * request that never settles kept the re-check pending, so Attach stayed held
 * and every later town or date change queued behind it. 60 s is above the
 * function's worst case (the session check, the admin check, the candidate
 * read and its two RPCs: five sequential 10 s budgets), so a request the desk
 * gave up on has, in practice, finished on the server before the next one is
 * sent (D-10).
 */
export const RECHECK_CLIENT_TIMEOUT_MS = 60_000

/** The function's text cap (parseRecheckBody refuses longer). */
const TEXT_MAX = 300

/**
 * D-05: an undecided editor row the editor left unchecked, or one the desk
 * already re-checked (desk_rules, any label). An editor rules call with a
 * label is never re-run by the desk. The function and the RPC check it again.
 */
export const isRecheckEligible = (
  row: Pick<ApplicationRow, 'processed' | 'editor_version' | 'match_label' | 'match_by'>
): boolean => {
  if (row.processed) return false
  if (row.editor_version === null) return false
  if (row.match_by === DESK_RULES_MATCH_BY) return true
  return row.match_label === 'unchecked'
}

/**
 * A form date in UTC, or null when it is missing or invalid. Mid-typing, the
 * date picker reports an invalid Date (e.g. year 0000); that is no date, never
 * the string "Invalid Date" (UAT 33.1, 1886).
 */
const utcOrNull = (value: Date | null | undefined): dayjs.Dayjs | null => {
  if (!value) return null
  const day = dayjs.utc(value)
  return day.isValid() ? day : null
}

/**
 * D-28: the town and start day the re-check depends on. The form holds the
 * Lisbon wall-clock digits stamped as UTC (D-25), so the day is read in UTC:
 * a 00:30 start stays on its own day in every browser time zone.
 */
export const recheckKeyOf = (values: Pick<DeskWatchedValues, 'cityId' | 'startTime'>): string => {
  const day = utcOrNull(values.startTime)?.format('YYYY-MM-DD') ?? ''
  return `${values.cityId ?? ''}|${day}`
}

const text = (value: string | undefined): string | null => {
  const trimmed = (value ?? '').trim()
  return trimmed.length > 0 ? trimmed.slice(0, TEXT_MAX) : null
}

/**
 * The function's body from the form's last report. `sides` are the sides
 * buildDeskPrefill put the original title and subtitle on (deskSourceSides),
 * so each is sent in the source's own language, as the editor matched it.
 *
 * `placeholderStartTime` is deskPrefill's START_TIME_PLACEHOLDER when
 * `timeNotStated(row).start` (the post states the date but not the time, so
 * the form opened on that placeholder), else null. While the form still shows
 * the placeholder the time is sent as null, because the editor matched the
 * candidate with no start time; sending 00:00 would turn a duplicate into a
 * possible update under a rules revision whose startTimeIsUpdate is on (D-15
 * parity, code review WR-02). A time she typed over it is sent. The caller
 * passes the constant: deskPrefill imports franc-min (ESM only), which this
 * barrel must not pull in.
 */
export const buildRecheckBody = (
  applicationId: number,
  values: DeskWatchedValues,
  sides: { title: boolean, subtitle: boolean },
  placeholderStartTime: string | null = null
): DeskRecheckBody => {
  const start = utcOrNull(values.startTime)
  const placeholderTime = placeholderStartTime !== null && start !== null && start.format('HH:mm:ss') === placeholderStartTime
  const finish = utcOrNull(values.matchFields?.finishTime)
  const fields = values.matchFields
  return {
    application_id: applicationId,
    city_id: values.cityId,
    start_date: start ? start.format('YYYY-MM-DD') : null,
    end_date: finish ? finish.format('YYYY-MM-DD') : null,
    start_time: start && !placeholderTime ? start.format('HH:mm') : null,
    title: text(sides.title ? fields?.title_pt : fields?.title),
    subtitle: text(sides.subtitle ? fields?.subTitle_pt : fields?.subTitle)
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isNullableId = (value: unknown): boolean =>
  value === null || (typeof value === 'number' && Number.isSafeInteger(value))

const isTarget = (value: unknown): boolean => {
  if (value === null) return true
  if (!isRecord(value)) return false
  return (
    (value.kind === 'event' || value.kind === 'application') &&
    typeof value.id === 'number' &&
    Number.isSafeInteger(value.id)
  )
}

/** Every field of the 200 contract, including "reason iff unchecked". */
const isRecheckResponse = (data: unknown): data is DeskRecheckResponse => {
  if (!isRecord(data)) return false
  if (typeof data.label !== 'string' || !LABELS.includes(data.label)) return false
  if (!isTarget(data.target)) return false
  if (data.label === 'unchecked') {
    if (typeof data.reason !== 'string' || !(RECHECK_REASONS as ReadonlyArray<string>).includes(data.reason)) {
      return false
    }
  } else if (data.reason !== null) {
    return false
  }
  if (typeof data.written !== 'boolean') return false
  const checked = data.checked
  if (!isRecord(checked)) return false
  if (!isNullableId(checked.city_id)) return false
  return checked.start_date === null || typeof checked.start_date === 'string'
}

const isErrorCode = (code: unknown): code is (typeof ERROR_CODES)[number] =>
  typeof code === 'string' && (ERROR_CODES as ReadonlyArray<string>).includes(code)

/**
 * supabase-js answers a non-2xx with a FunctionsHttpError (or a relay error)
 * whose `context` is the Response; a fetch failure has none. Only a code from
 * the contract is kept; the message is never read (codes only, T-33.1-17).
 */
const readRefusal = async (error: unknown): Promise<DeskRecheckOutcome> => {
  const context = (error as { context?: unknown } | null)?.context as
    | { status?: unknown; json?: () => Promise<unknown> }
    | undefined
  if (!context || typeof context.json !== 'function') {
    return { ok: false, code: 'network', status: null }
  }
  const status = typeof context.status === 'number' ? context.status : null
  let body: unknown = null
  try {
    body = await context.json()
  } catch {
    body = null
  }
  const code = isRecord(body) && isErrorCode(body.code) ? body.code : 'unknown'
  return { ok: false, code, status }
}

/**
 * The one door to desk-recheck-match. Never throws. Bounded by
 * RECHECK_CLIENT_TIMEOUT_MS: the abort rejects the invoke (AbortError), which
 * is `network`. An AbortController and a timer rather than
 * AbortSignal.timeout, so the timer is cleared as soon as the call settles.
 */
export const recheckMatch = async (body: DeskRecheckBody): Promise<DeskRecheckOutcome> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), RECHECK_CLIENT_TIMEOUT_MS)
  try {
    const { data, error } = await supabaseClient.functions.invoke('desk-recheck-match', {
      body,
      signal: controller.signal
    })
    if (error) return await readRefusal(error)
    if (!isRecheckResponse(data)) return { ok: false, code: 'unknown', status: null }
    return { ok: true, value: data }
  } catch {
    return { ok: false, code: 'network', status: null }
  } finally {
    clearTimeout(timer)
  }
}
